import React from 'react';
import { Placeholder, Text, Image, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import './styles.css';
import '../FullWidthRichText/subtitle.css';
import '../FullWidthRichText/typography.css';
import RenderingStyling from '../../../lib/RenderingStyling';
import BackgroundImageStyles from '../FullWidthRichText/backgroundImageStyles';
import {
  renderSubTitle,
  renderFeaturedHeading,
  renderPrimaryHeading,
  renderSecondaryHeading,
  renderText,
  renderCtaLink,
} from '../FullWidthRichText/fieldsRenderer';
import {
  addStyle,
  getTrackingAttributes,
  getTrackingSecondaryAttributes,
} from '../SharedUtils/utils';
import { useSubtitleClasses } from '../FullWidthRichText/useSubtitleClasses';

import { addBackgroundColorVariables } from '../SharedUtils/themeColors';

import { useTheme, useInvertedColor } from '../SharedUtils/hooks';

const FullWidthRichTextWithSubcomponent = ({ fields, rendering, sitecoreContext }) => {
  const isEditorMode = sitecoreContext.pageEditing;

  const componentId = `c-${rendering.uid}`;

  const { isLight } = useTheme();

  const widgetStyles = {};
  const widgetClasses = ['o-widget', 'c-full-width-text-subcontent', 'background-color-theme'];
  const subTitleClasses = useSubtitleClasses('c-full-width-text-subcontent__subtitle', fields);

  const isCtaInverted = isLight
    ? fields.CTAPrimaryBlueBorderButtonStyleLight?.value
    : fields.CTAPrimaryBlueBorderButtonStyleDark?.value;
  const isSecondCtaInverted = isLight
    ? fields.CTASecondaryBlueBorderButtonStyleLight?.value
    : fields.CTASecondaryBlueBorderButtonStyleDark?.value;

  const isColorInverted = useInvertedColor(fields);
  addBackgroundColorVariables(fields, widgetStyles);

  const isNarrowContainer = fields.NarrowContainer?.value;

  let textCTAPosition = '';
  if (!fields.IsRichTextBefore?.value && !fields.IsCallToActionBefore?.value) {
    textCTAPosition = 'text-cta-after';
  } else if (fields.IsRichTextBefore?.value && !fields.IsCallToActionBefore?.value) {
    textCTAPosition = 'text-before-cta-after';
  } else if (!fields.IsRichTextBefore?.value && fields.IsCallToActionBefore?.value) {
    textCTAPosition = 'text-after-cta-before';
  }

  addStyle(widgetStyles, '--full-width-text-subcontent-padding-top', fields.PaddingTop?.value);
  addStyle(
    widgetStyles,
    '--full-width-text-subcontent-padding-bottom',
    fields.PaddingBottom?.value
  );
  addStyle(
    widgetStyles,
    '--full-width-text-subcontent-logo-image-max-width',
    fields.LogoMaxWidth?.value
  );
  addStyle(widgetStyles, '--full-width-text-subcontent-logo-image-width', fields.LogoWidth?.value);
  addStyle(
    widgetStyles,
    '--full-width-text-subcontent-height',
    fields.Height?.value ? `${fields.Height.value}px` : null
  );

  if (fields.BackgroundImageCover?.value) widgetClasses.push('has-background-covered');
  if (fields.IsPrimaryHeadingBottomBorder?.value) widgetClasses.push('has-border');
  if (isColorInverted) widgetClasses.push('is-inverted');
  if (fields.ImageHorizontalAlignment?.fields?.value?.value)
    widgetClasses.push(fields.ImageHorizontalAlignment.fields.value.value);
  if (fields.ImageVerticalAlignment?.fields?.value?.value)
    widgetClasses.push(fields.ImageVerticalAlignment.fields.value.value);
  if (fields.EnableMarginBottom?.value) widgetClasses.push('has-margin-bottom');
  if (fields.TextAlignCentered?.value) widgetClasses.push('u-text-center');

  const trackingAttributes = getTrackingAttributes(fields);
  const secondaryTrackingAttributes = getTrackingSecondaryAttributes(fields);

  const featuredHeading = () => {
    return renderFeaturedHeading({
      field: fields.FeatureHeading,
      classes:
        'c-full-width-text-subcontent__feature u-text-feature-title-lg u-text-feature-title-md u-text-feature-title-sm',
    });
  };

  const primaryHeading = () => {
    return renderPrimaryHeading({
      fields: fields,
      classes: 'c-full-width-text-subcontent__primary',
    });
  };

  const secondaryHeading = () => {
    return renderSecondaryHeading({
      field: fields.SecondaryHeading,
      classes: 'c-full-width-text-subcontent__secondary',
    });
  };

  const renderSecondaryHeadingAlternate = () => {
    if (!isEditorMode && !fields.SecondaryHeading?.value) {
      return <></>;
    }

    return (
      <div
        className="c-full-width-text-subcontent__secondary u-text-lg-2 u-text-md-2 u-text-sm-2"
        data-component="SecondaryHeading"
        tabIndex="0"
      >
        <Text field={fields.SecondaryHeading}></Text>
      </div>
    );
  };

  const text = () => {
    return renderText({
      field: fields.Text,
      classes: 'c-full-width-text-subcontent__description',
      isNarrow: isNarrowContainer,
    });
  };

  const renderCtaButtons = () => {
    return (
      <>
        {renderCtaLink({
          field: fields.CallToAction,
          classes: 'c-full-width-text-subcontent__cta',
          isInverted: isCtaInverted,
          tracking: trackingAttributes,
        })}
        {renderCtaLink({
          field: fields.CallToActionSecondary,
          classes: 'c-full-width-text-subcontent__cta',
          isInverted: isSecondCtaInverted,
          tracking: secondaryTrackingAttributes,
        })}
      </>
    );
  };

  const renderPlaceholder = () => {
    return (
      <div className="c-full-width-text-subcontent__placeholder">
        <Placeholder name="jss-sub-content" rendering={rendering} />
      </div>
    );
  };

  const renderDifferentCtaPositions = () => {
    switch (textCTAPosition) {
      case 'text-cta-after':
        return (
          <>
            {renderPlaceholder()}
            {text()}
            {renderCtaButtons()}
          </>
        );
      case 'text-before-cta-after':
        return (
          <>
            {text()}
            {renderPlaceholder()}
            {renderCtaButtons()}
          </>
        );
      case 'text-after-cta-before':
        return (
          <>
            {renderCtaButtons()}
            {renderPlaceholder()}
            {text()}
          </>
        );
    }

    return (
      <>
        {text()}
        {renderCtaButtons()}
        {renderPlaceholder()}
      </>
    );
  };

  return (
    <div
      id={componentId}
      className={widgetClasses.join(' ')}
      style={widgetStyles}
      {...RenderingStyling(rendering.componentName)}
    >
      <div className="c-container">
        <div className="full-width-wrapper">
          {renderSubTitle({ field: fields.SubTitle, classes: subTitleClasses })}

          {(isEditorMode || fields.Image?.value?.src) && (
            <div
              className={
                'c-full-width-text-subcontent__LogoImage' +
                (fields.Image?.value?.class === 'scEmptyImage' ? ' small' : '')
              }
              tabIndex="0"
            >
              <Image media={fields.Image} />
            </div>
          )}

          {!fields.IsPrimaryHeadingBefore?.value && (
            <>
              {renderPlaceholder()}

              {featuredHeading()}

              {primaryHeading()}

              {renderSecondaryHeadingAlternate()}

              {text()}

              {renderCtaButtons()}
            </>
          )}

          {fields.IsPrimaryHeadingBefore?.value && (
            <>
              {featuredHeading()}

              {primaryHeading()}

              {secondaryHeading()}

              {renderDifferentCtaPositions()}
            </>
          )}
        </div>
      </div>

      <BackgroundImageStyles componentId={componentId} fields={fields} isLight={isLight} />
    </div>
  );
};

export default withSitecoreContext()(FullWidthRichTextWithSubcomponent);
